import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { UserXIcon } from 'lucide-react';
import { useAdmin } from '../AdminContext';
import { btnGhost, btnPrimary, btnSecondary } from '../ui/styles';
import { choiceState } from '../../ui/styles';
import { newRequestId } from '../../core/api/http';
import { absencePlan, withException, type AbsenceAction } from '../../core/rules/absence';
import { OWNER_STAFF_ID, staffName } from '../../core/rules/salonDays';
import { today } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import type { AgendaControl } from './useAgenda';
import type { AgendaAppointment } from '../../core/types/agenda';
import type { StaffProfile } from '../../core/types/site';
type Props = {
  date: string;
  /** Wie er volgens de salondag werkt. */
  staffId: string;
  team: StaffProfile[];
  /** Alle afspraken van de week (ongefilterd), zodat "Iedereen / per persoon" niets verbergt. */
  appointments: AgendaAppointment[];
  agenda: AgendaControl;
};

/**
 * "Mila is ziek" — in het dagoverzicht, alleen voor Sarah en vanaf vandaag. Eén keuze voor de hele
 * dag: Sarah neemt over (zelfde tijden, de klanten krijgen een nieuwe uitnodiging) of afzeggen (de
 * salon is dicht, de klanten krijgen een mail met een link om te verzetten).
 */
export function AbsencePanel({
  date,
  staffId,
  team,
  appointments,
  agenda
}: Props) {
  const id = useId();
  const {
    site,
    applyServerSite
  } = useAdmin();
  const [open, setOpen] = useState(false);
  const plan = absencePlan(appointments, date, staffId, site.openingHours, site.salonExceptions, today());
  const [action, setAction] = useState<AbsenceAction>(plan.ownerCanTakeOver ? 'naar-eigenaar' : 'afzeggen');
  const [busy, setBusy] = useState(false);
  const request = useRef(newRequestId());
  const name = staffName(team, staffId);
  const owner = staffName(team, OWNER_STAFF_ID);
  if (plan.blocked) return null;
  const n = plan.affected.length;
  const options: {
    key: AbsenceAction;
    label: string;
    text: string;
  }[] = [...(plan.ownerCanTakeOver ? [{
    key: 'naar-eigenaar' as const,
    label: `${owner} neemt over`,
    text: n ? `${plural(n, 'afspraak gaat', 'afspraken gaan')} naar ${owner}, op dezelfde tijd. De klanten krijgen bericht.` : `${owner} werkt deze dag.`
  }] : []), {
    key: 'afzeggen',
    label: 'Afzeggen',
    text: n ? `De salon is dicht. ${plural(n, 'klant krijgt', 'klanten krijgen')} een mail dat de afspraak niet doorgaat, met een link om te verzetten.` : 'De salon is deze dag dicht.'
  }];
  const confirm = async () => {
    setBusy(true);
    const result = await agenda.reportAbsence({
      requestId: request.current,
      date,
      staffId,
      action
    });
    setBusy(false);
    if (!result) return;
    // De server legde de afwijkende dag vast: meteen zichtbaar (ook in Instellingen), maar niet als
    // "niet opgeslagen", en Verwerpen haalt hem niet weg. Andere open wijzigingen blijven staan.
    applyServerSite(s => ({
      ...s,
      salonExceptions: withException(s.salonExceptions, result.exception)
    }), result.revision);
    request.current = newRequestId();
    setOpen(false);
    toast.success(action === 'naar-eigenaar' ? `${owner} neemt de dag over` : 'Dag afgezegd', {
      description: action === 'naar-eigenaar' ? `${plural(result.moved.length, 'afspraak', 'afspraken')} verzet naar ${owner}.` : `${plural(result.cancelled.length, 'klant krijgt', 'klanten krijgen')} bericht met een link om te verzetten.`
    });
  };
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className={`${btnGhost} -ml-2`} data-id="element-120" data-mp="s1l8l03sn124">
        <UserXIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-121" data-mp="n90k8e3npg5z" />
        {name} afwezig melden
      </button>;
  }
  return <fieldset className="rounded-md border border-line p-3" data-id="element-122" data-mp="i8mkdjc7lcj1">
      <legend className="px-1 text-[13px] font-medium text-ink" data-id="element-123" data-mp="1fr622cxkpgpn">{name} kan deze dag niet</legend>
      <p className="text-[13px] text-muted" data-id="element-124" data-mp="1aycy3r9xicem">{n ? `${plural(n, 'open afspraak', 'open afspraken')} bij ${name}.` : `Geen open afspraken bij ${name}.`}</p>
      <div role="radiogroup" aria-labelledby={`${id}-l`} className="mt-3 space-y-2" data-id="element-125" data-mp="um81zik11jy7">
        <span id={`${id}-l`} className="sr-only" data-id="element-126" data-mp="zgvl11w9hm5e">
          Wat gebeurt er met de dag?
        </span>
        {options.map(o => <button key={o.key} type="button" role="radio" aria-checked={action === o.key} onClick={() => setAction(o.key)} className={`block w-full rounded-sm px-3 py-2.5 text-left ${choiceState(action === o.key)}`} data-id="element-127" data-mp="qhrzzsdjawo9">
            <span className="block text-[14px]" data-id="element-128" data-mp="14ikxmlz4u7vl">{o.label}</span>
            <span className="mt-0.5 block text-[12px] leading-snug opacity-80" data-id="element-129" data-mp="14fypol8bxqcu">{o.text}</span>
          </button>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-id="element-130" data-mp="1x66znpspp4gc">
        <button type="button" onClick={() => void confirm()} disabled={busy} aria-busy={busy} className={btnPrimary} data-id="element-131" data-mp="1x5fel8wqqp4b">
          {busy ? 'Bezig…' : action === 'naar-eigenaar' ? `${owner} neemt over` : 'Dag afzeggen'}
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy} className={btnSecondary} data-id="element-132" data-mp="27andkdmmsbhi">
          Annuleren
        </button>
      </div>
    </fieldset>;
}